import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, Search, Filter, Lock, Crown, Package } from 'lucide-react';
import axios from 'axios';
import { useI18n } from '@/i18n';
import { getCurrentTier, canAccess, TIERS } from '@/tiers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ExerciseLibrary = () => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const userTier = getCurrentTier();

  const [exercises, setExercises] = useState([]);
  const [translatedTitles, setTranslatedTitles] = useState({});
  const [skillCategories, setSkillCategories] = useState([]);
  const [difficultyLevels, setDifficultyLevels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [loading, setLoading] = useState(true);

  // Fetch skill categories and difficulty levels on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [skillsRes, difficultiesRes] = await Promise.all([
          axios.get(`${API}/skills`),
          axios.get(`${API}/difficulties`)
        ]);
        setSkillCategories(skillsRes.data);
        setDifficultyLevels(difficultiesRes.data);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };
    fetchFilters();
  }, []);

  // Fetch all exercises (with filters) — always all tiers, but mark locked ones in UI
  useEffect(() => {
    const fetchExercises = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (selectedSkill && selectedSkill !== 'all') params.append('skill', selectedSkill);
        if (selectedDifficulty && selectedDifficulty !== 'all') params.append('difficulty', selectedDifficulty);
        if (searchQuery) params.append('search', searchQuery);
        const response = await axios.get(`${API}/exercises?${params.toString()}`);
        setExercises(response.data);
      } catch (error) {
        console.error('Error fetching exercises:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchExercises();
  }, [selectedSkill, selectedDifficulty, searchQuery]);

  // For non-English: lazily fetch translated titles/descriptions
  useEffect(() => {
    if (lang === 'en' || exercises.length === 0) {
      setTranslatedTitles({});
      return;
    }
    const fetchTranslations = async () => {
      const result = {};
      // Fetch in parallel but ignore failures (fallback to English)
      await Promise.all(exercises.map(async (ex) => {
        try {
          const res = await axios.get(`${API}/exercises/${ex.id}/translate?lang=${lang}`);
          result[ex.id] = {
            title: res.data.title,
            description: res.data.description,
          };
        } catch (e) {
          // keep English fallback
        }
      }));
      setTranslatedTitles(result);
    };
    fetchTranslations();
  }, [lang, exercises]);

  const handleReset = () => {
    setSearchQuery('');
    setSelectedSkill('all');
    setSelectedDifficulty('all');
  };

  const tierBadge = (exTier) => {
    if (exTier === 'pro') return (
      <Badge className="bg-orange-500 text-white border-0 text-xs font-bold">
        <Crown className="h-3 w-3 mr-1" />
        {t('proPlan')}
      </Badge>
    );
    if (exTier === 'ultimate') return (
      <Badge className="bg-purple-600 text-white border-0 text-xs font-bold">
        <Package className="h-3 w-3 mr-1" />
        {t('ultimatePlan')}
      </Badge>
    );
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 via-white to-blue-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-6xl font-black text-gray-900 mb-4">
            {t('exerciseLibrary')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('browseAll')}
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-12 border-2 border-orange-200 shadow-lg bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl font-bold text-gray-900">
              <Filter className="h-6 w-6 text-orange-600" />
              {t('filterExercises')}
            </CardTitle>
            <CardDescription className="text-base">
              {t('findPerfect')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-base border-2 border-gray-300 focus:border-orange-500 rounded-lg"
                data-testid="search-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('skillCategory')}</label>
                <Select value={selectedSkill} onValueChange={setSelectedSkill}>
                  <SelectTrigger className="h-12 text-base border-2 border-gray-300 focus:border-orange-500 rounded-lg" data-testid="skill-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {skillCategories.map(category => (
                      <SelectItem key={category.value} value={category.value} className="text-base">
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('difficultyLevel')}</label>
                <Select value={selectedDifficulty} onValueChange={setSelectedDifficulty}>
                  <SelectTrigger className="h-12 text-base border-2 border-gray-300 focus:border-orange-500 rounded-lg" data-testid="difficulty-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {difficultyLevels.map(level => (
                      <SelectItem key={level.value} value={level.value} className="text-base">
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end">
              <Button 
                variant="outline" 
                onClick={handleReset}
                className="border-2 border-orange-500 text-orange-600 hover:bg-orange-50 font-semibold transition-colors"
                data-testid="reset-filters-btn"
              >
                {t('resetFilters')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <p className="text-lg font-semibold text-gray-700">
            {t('showing')} <span className="text-orange-600">{exercises.length}</span> {exercises.length !== 1 ? t('exercisesPlural') : t('exercise')}
          </p>
          {userTier === TIERS.BASIC && (
            <Link to="/pricing">
              <Button variant="outline" className="border-2 border-orange-400 text-orange-600 hover:bg-orange-50 font-bold" data-testid="upgrade-cta">
                <Crown className="h-4 w-4 mr-1" />
                {t('upgradeToPro')}
              </Button>
            </Link>
          )}
        </div>

        {/* Exercise Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-xl text-gray-600">{t('loading')}</p>
          </div>
        ) : exercises.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {exercises.map((exercise) => {
              const accessible = canAccess(userTier, exercise.tier);
              const title = (translatedTitles[exercise.id] && translatedTitles[exercise.id].title) || exercise.title;
              const description = (translatedTitles[exercise.id] && translatedTitles[exercise.id].description) || exercise.description;
              return (
                <Card
                  key={exercise.id}
                  className={`border-2 transition-all duration-200 bg-white overflow-hidden relative ${
                    accessible
                      ? 'hover:border-orange-400 hover:shadow-2xl transform hover:-translate-y-2'
                      : 'border-gray-200 opacity-90'
                  }`}
                  data-testid={`exercise-card-${exercise.id}`}
                >
                  {!accessible && (
                    <div className="absolute top-3 right-3 z-10">
                      <div className="bg-gray-900/90 text-white p-2 rounded-full">
                        <Lock className="h-4 w-4" />
                      </div>
                    </div>
                  )}
                  <CardHeader className="space-y-3">
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200 font-semibold border border-orange-300 text-xs sm:text-sm">
                        {exercise.skill}
                      </Badge>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {tierBadge(exercise.tier)}
                        <Badge 
                          variant="outline" 
                          className={`font-semibold text-xs sm:text-sm ${
                            exercise.difficulty === 'Beginner' ? 'border-green-500 text-green-700 bg-green-50' :
                            exercise.difficulty === 'Intermediate' ? 'border-blue-500 text-blue-700 bg-blue-50' :
                            'border-red-500 text-red-700 bg-red-50'
                          }`}
                        >
                          {exercise.difficulty}
                        </Badge>
                      </div>
                    </div>
                    <CardTitle className={`text-xl font-bold leading-tight ${accessible ? 'text-gray-900' : 'text-gray-500'}`}>
                      {title}
                    </CardTitle>
                    <CardDescription className="text-base text-gray-600 line-clamp-3">
                      {description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="text-sm text-gray-600 font-medium">
                        <span className="text-orange-600 font-semibold">{t('duration')}:</span> {exercise.duration}
                      </div>
                      {accessible ? (
                        <Link to={`/exercise/${exercise.id}`}>
                          <Button className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 rounded-lg transition-all duration-200 transform hover:scale-105">
                            {t('viewDetails')}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        </Link>
                      ) : (
                        <Button
                          onClick={() => navigate('/pricing')}
                          className="w-full bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-800 hover:to-black text-white font-bold py-2.5 rounded-lg transition-all"
                          data-testid={`upgrade-${exercise.id}`}
                        >
                          <Lock className="mr-2 h-4 w-4" />
                          {exercise.tier === 'pro' ? t('upgradeToPro') : t('upgradeToUltimate')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-2 border-gray-200 bg-white p-12">
            <div className="text-center space-y-4">
              <div className="text-6xl">⚾</div>
              <h3 className="text-2xl font-bold text-gray-900">{t('noExercises')}</h3>
              <p className="text-gray-600">{t('tryAdjusting')}</p>
              <Button 
                onClick={handleReset}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold"
              >
                {t('resetFilters')}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};
